/**
 * Frontend view types + PURE display helpers for the call-centre Daily
 * report (CALLCENTRE-EOD-V1). Mirrors the backend
 * lib/callcenter-daily-report.ts / api/routes/callcenter-daily-report.ts
 * shapes (the sla-leakage-view.ts precedent: a frontend mirror, never a
 * cross-build-target import -- frontend and backend are separate projects).
 * Side-effect-free (no React, no CSS) so render tests import these directly.
 *
 * PALETTE (docs/design/design-language/02-DESIGN-TOKENS.md section 10b,
 * RATIFIED 2026-08-25): this report renders NO state chip from the 14-state
 * matrix -- none of its vocabulary (SLA breach classes, chase classes,
 * lead_events labels) is a member of that matrix, which governs the
 * appointment/treatment-plan lifecycle, a different vocabulary. It DOES use
 * the URGENCY ramp for SLA-breach severity, reusing exactly TWO of its four
 * steps -- 'soon' (amber) and 'breached' (red) -- because the backend
 * classifier (lib/sla-leakage/sla-leakage-engine.ts itemSeverity) only ever
 * distinguishes two non-neutral levels; inventing a third ('now') to fill
 * the ramp would be exactly the divergence the matrix forbids. Urgency never
 * renders inside a state chip and never stacks two treatments on one row --
 * both ratified rules.
 */

import type { SlaBreachClass, SlaSeverity } from '@/dashboard/sla-leakage/sla-leakage-view';
import { classMeta, classShortLabel, formatLeakageAge } from '@/dashboard/sla-leakage/sla-leakage-view';

export type { SlaBreachClass, SlaSeverity };
export { classMeta, classShortLabel, formatLeakageAge };

// ---- Activity vocabulary (mirrors lib/callcenter-daily-report.ts) ----------

export type ActivityCategory =
  | 'contact_attempt'
  | 'closed_unreachable'
  | 'qualified'
  | 'appointment_booked'
  | 'lost'
  | 'converted_to_revenue'
  | 'ownership_change';

export const ACTIVITY_CATEGORY_LABEL: Readonly<Record<ActivityCategory, string>> = {
  contact_attempt: 'Contact attempts',
  appointment_booked: 'Appointments booked',
  qualified: 'Leads qualified',
  closed_unreachable: 'Closed - not reachable / duplicate',
  lost: 'Leads closed - lost',
  converted_to_revenue: 'Converted to revenue',
  ownership_change: 'Ownership changes',
};

export interface ActivityTypeRowView {
  readonly eventType: string;
  readonly category: ActivityCategory;
  readonly label: string;
  readonly count: number;
}

export interface ActivityEntryView {
  readonly safeLeadId: string;
  readonly displayName: string;
  readonly clinicLabel: string | null;
  readonly eventType: string;
  readonly label: string;
  readonly occurredAtIso: string;
}

export interface ActivitySummaryView {
  readonly totalCount: number;
  readonly distinctLeadsTouched: number;
  readonly byType: readonly ActivityTypeRowView[];
  readonly entries: readonly ActivityEntryView[];
  readonly entriesTotalCount: number;
}

// ---- Backlog (TODAY only) ---------------------------------------------------

export type ChaseClass = 'enquired_never_booked' | 'booked_never_visited';

export interface ChaseClassRowView {
  readonly chaseClass: ChaseClass;
  readonly count: number;
}

export interface SlaClassSummaryView {
  readonly breachClass: SlaBreachClass;
  readonly count: number;
  readonly severity: SlaSeverity;
}

export interface LiveBacklogView {
  readonly available: boolean;
  readonly chase: {
    readonly totalCount: number;
    readonly byClass: readonly ChaseClassRowView[];
    readonly capped: boolean;
  } | null;
  readonly slaBreach: {
    readonly totalCount: number;
    readonly byClass: readonly SlaClassSummaryView[];
    readonly capped: boolean;
  } | null;
}

export interface CallcenterDailyAgentReportWire {
  readonly reportKind: 'agent';
  readonly businessDate: string;
  readonly todayBusinessDate: string;
  readonly isToday: boolean;
  readonly generatedAtIso: string;
  readonly generatedAtZone: string;
  readonly ownLeadsTotalCount: number;
  readonly ownLeadsCapped: boolean;
  readonly activity: ActivitySummaryView;
  readonly liveBacklog: LiveBacklogView;
}

// ---- Manager rollup (AGENT-DAY-PRODUCTIVITY-ROLLUP-V1, activity 11) --------
// Mirrors api/routes/callcenter-daily-report.ts's CallcenterDailyRollupResult.
// A DISJOINT shape from CallcenterDailyAgentReportWire above -- the backend
// branches on caller role onto the SAME endpoint, never a second URL.

export interface CallcenterDailyRollupAgentRowView {
  readonly staffId: number;
  readonly staffLabel: string;
  readonly activity: ActivitySummaryView;
}

export interface CallcenterDailyRollupWire {
  readonly reportKind: 'rollup';
  readonly businessDate: string;
  readonly todayBusinessDate: string;
  readonly isToday: boolean;
  readonly generatedAtIso: string;
  readonly generatedAtZone: string;
  readonly orgLeadsTotalCount: number;
  readonly orgLeadsCapped: boolean;
  readonly agents: readonly CallcenterDailyRollupAgentRowView[];
  readonly orgTotal: ActivitySummaryView;
}

/**
 * The fetchCallcenterDailyReport() return type (frontend/src/dashboard/api.ts,
 * outside this package's allowed-files fence -- NOT edited here). Redefining
 * this EXISTING exported name as the union (rather than introducing a second
 * type name) means api.ts's `Promise<CallcenterDailyReportWire>` signature
 * stays correct, unedited, with zero changes on its side: the backend now
 * genuinely returns one of two disjoint shapes depending on caller role, and
 * this type says so.
 */
export type CallcenterDailyReportWire = CallcenterDailyAgentReportWire | CallcenterDailyRollupWire;

export function isRollupWire(wire: CallcenterDailyReportWire): wire is CallcenterDailyRollupWire {
  return wire.reportKind === 'rollup';
}

// ---- Urgency ramp (docs/design/design-language/02-DESIGN-TOKENS.md 10b.1) --

export type UrgencyStep = 'none' | 'soon' | 'breached';

/** neutral -> none, amber -> soon, red -> breached. Deliberately never 'now'
 *  -- see the module header. */
export function urgencyStepForSeverity(severity: SlaSeverity): UrgencyStep {
  if (severity === 'red') return 'breached';
  if (severity === 'amber') return 'soon';
  return 'none';
}

/** KpiTile tone this report's summary tiles use for a class's severity --
 *  the SAME tone vocabulary ManagerOpsCockpit already renders SLA classes
 *  with (frontend/src/dashboard/manager-tracking/ManagerOpsCockpit.tsx), so
 *  this surface introduces no second severity-to-tone mapping. */
export function kpiToneForSeverity(severity: SlaSeverity): 'ok' | 'warm' | 'breach' {
  if (severity === 'red') return 'breach';
  if (severity === 'amber') return 'warm';
  return 'ok';
}

// ---- Pure date helpers (single IST business date; never a range) ----------

const BUSINESS_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export function isValidBusinessDate(v: string): boolean {
  return BUSINESS_DATE_RE.test(v);
}

/** Add whole days to a YYYY-MM-DD business date (pure calendar arithmetic --
 *  mirrors lib/owner-money-console.ts addBusinessDays; the IST day-boundary
 *  conversion itself stays server-side via istDayWindow). */
export function addBusinessDays(businessDate: string, days: number): string {
  const d = new Date(`${businessDate}T00:00:00.000Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

const DAY_LABEL_FORMAT = new Intl.DateTimeFormat('en-IN', {
  timeZone: 'Asia/Kolkata',
  weekday: 'short',
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});

/** "Mon, 25 Aug 2026" for a YYYY-MM-DD business date. */
export function dayLabel(businessDate: string): string {
  return DAY_LABEL_FORMAT.format(new Date(`${businessDate}T06:00:00.000Z`));
}

/** One-line proportion caption, e.g. "3 of 40 (8%)". Denominator 0 -> just
 *  the count (never a divide-by-zero percent, never a fake 0%). */
export function proportionLabel(count: number, total: number): string {
  if (total <= 0) return String(count);
  const pct = Math.round((count / total) * 100);
  return `${count} of ${total} (${pct}%)`;
}

// W2 period projection: mirrors the existing authorized report handler.
export interface PeriodCountView {
  status: 'complete' | 'partial' | 'unavailable'; count: number | null;
  observedCount: number; reason: string | null;
  details: readonly { id: number; leadId: number | null; occurredAt: string }[];
}
export interface PeriodReportQuery {
  periodStart: string; periodEnd: string; observedAt?: string;
  agentId?: number; clinicId?: number; source?: string;
}
export type PeriodMetric = 'bookings' | 'attendance' | 'outbound_calls' | 'connected_calls' | 'missed_calls' | 'activity' | 'first_attempt';
export interface PeriodReportView {
  reportKind: 'period'; exportable: boolean;
  sourceBreakdown?: { status: 'complete' | 'unavailable'; reason: string | null; rows: readonly { key: string; kind: 'source' | 'unknown' | 'unlinked'; source: string | null; newEnquiries: number; bookings: Omit<PeriodCountView,'details'>; attendance: Omit<PeriodCountView,'details'> }[] };
  firstActualAttempt: {
    status: string; reason: string | null; observedAt: string;
    period: { from: string; through: string; timezone: string; startIso: string; endIso: string };
    filters: { agentId?: number; clinicId?: number; source?: string };
    measure: null | { totalCohort: number; attempted: number; noAttempt: number; dataUnavailable: number; delaySampleCount: number; meanDelayMs: number | null; rows: readonly {leadId:number;state:string;delayMs:number|null}[] };
  };
  bookingCount: PeriodCountView; attendanceCount: PeriodCountView;
  recorded: {
    providerCalls: { outboundAttempts: PeriodCountView; connectedCalls: PeriodCountView; missedInboundCalls: PeriodCountView; currentAssignee?: { status: 'complete' | 'unavailable'; rows: readonly {kind:'agent'|'unassigned'|'unlinked';staffId:number|null;outboundAttempts:PeriodCountView;connectedCalls:PeriodCountView;missedInboundCalls:PeriodCountView}[] } };
    recordedActivity: { status: string; currentAssignee?: {byAgent:readonly {staffId:number;staffLabel:string;activity:{totalCount:number}}[];orgTotal:{totalCount:number}} | null; summary: null | { totalCount: number }; details: readonly { id:number;leadId:number;occurredAtIso:string;eventType:string }[] };
    sourceStates: Record<string,string>; population: string;
  };
  scope: {role: string; clinicIds: readonly number[]; ownStaffId: number | null};
  actorProductivity: {status:'unavailable';reason:string}; attribution: string;
}
export interface PeriodDetailView {
  metric: PeriodMetric; observedAt: string; totalObservedRows: number;
  rows: readonly {id:number;leadId:number|null;occurredAt:string|null;state:string;delayMs:number|null}[];
  nextCursor: string | null;
}
export function reportQueryString(q: PeriodReportQuery): string {
  const params = new URLSearchParams({periodStart:q.periodStart,periodEnd:q.periodEnd});
  for (const key of ['observedAt','agentId','clinicId','source'] as const) if(q[key] !== undefined) params.set(key,String(q[key]));
  return params.toString();
}
export function validReportRange(from: string, through: string): boolean {
  if(!isValidBusinessDate(from)||!isValidBusinessDate(through)||from>through)return false;
  if([from,through].some(date=>!Number.isFinite(Date.parse(date))||new Date(date+'T00:00:00Z').toISOString().slice(0,10)!==date))return false;
  return (Date.parse(through)-Date.parse(from))/86400000 < 366;
}
export function reportMonthRange(date: string): {from:string;through:string} {
  const [year,month]=date.split('-').map(Number);
  const last=new Date(Date.UTC(year!,month!,0)).getUTCDate();
  return {from:date.slice(0,7)+'-01',through:date.slice(0,7)+'-'+String(last).padStart(2,'0')};
}
export function reportCountLabel(metric: Omit<PeriodCountView,'details'> & Partial<Pick<PeriodCountView,'details'>>): string {
  return metric.status==='complete' && metric.count!==null ? metric.count.toLocaleString('en-IN') : metric.status==='partial' ? `${metric.observedCount.toLocaleString('en-IN')} observed` : 'Unavailable';
}
export function reportSnapshotQuery(wire: PeriodReportView): PeriodReportQuery {
  const a=wire.firstActualAttempt;
  return {periodStart:a.period.from,periodEnd:a.period.through,observedAt:a.observedAt,...a.filters};
}

/** Preserve exact source equality and selected deep links even for empty/error responses. */
export function reportSourceOptions(permitted: readonly string[], selected: string): string[] {
  return [...new Set([...permitted, selected].filter(value => value !== ''))];
}

/** Outcome cards share values but summary and full Reports have distinct reference orders. */
export function reportOutcomeTiles(wire: PeriodReportView, summary: boolean): {label:string;value:string;key:PeriodMetric|null}[] {
  const cards = [
    {label:'Leads',value:wire.firstActualAttempt.measure?.totalCohort.toLocaleString('en-IN')??'Unavailable',key:'first_attempt' as PeriodMetric},
    {label:'Bookings',value:reportCountLabel(wire.bookingCount),key:'bookings' as PeriodMetric},
    {label:'Clinic visits',value:reportCountLabel(wire.attendanceCount),key:'attendance' as PeriodMetric},
    {label:'Junk leads',value:'Unavailable',key:null},
  ];
  return summary ? [{...cards[0]!,label:'Leads'},cards[3]!,cards[1]!,{label:'Walk-ins',value:'Unavailable',key:null}] : [...cards,{label:'Call-to-booking rate',value:'Unavailable',key:null}];
}

/** Numeric presentation only: wire/count/export semantics remain unchanged. */
export function reportNumericPresentation(value: string | number): {text:string; explanation?:string} {
  if(typeof value==='number')return {text:value.toLocaleString('en-IN')};
  if(value==='Unavailable')return {text:'—',explanation:'Unavailable'};
  if(value==='N/A')return {text:'—',explanation:'Not applicable'};
  return {text:value.replace(/ observed$/, '')};
}

/** A known empty cohort has no percentage; an unknown/nonempty cohort lacks classification. */
export function reportJunkShareLabel(totalCohort: number | undefined): string {
  return totalCohort === 0 ? 'Not applicable' : 'Unavailable';
}
